/**
 * Compact popover that lets template authors pick a variable to insert into
 * an editable text field. Used by both `VariableTextInput` and
 * `VariableTextarea`, and exported standalone so other surfaces (e.g. a
 * floating toolbar) can reuse the catalog.
 */
import { useMemo, useState } from 'react';
import { ActionIcon, Popover, ScrollArea, Stack, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { IconBraces, IconSearch } from '@tabler/icons-react';

import {
  buildVariableToken,
  getRegisteredVariables,
  type VariableDefinition,
} from '../registry';

interface VariablePickerPopoverProps {
  /** Called when the user selects a variable. Receives the full token (`{{id}}`). */
  onPick: (token: string, definition: VariableDefinition) => void;
  /** Tooltip / aria-label override. */
  triggerLabel?: string;
  /** Optional set of variable ids to filter the list to (defaults to the full registry). */
  filterIds?: Set<string>;
  /** Width of the popover dropdown. */
  width?: number;
}

export function VariablePickerPopover({
  onPick,
  triggerLabel = 'Insert variable',
  filterIds,
  width = 320,
}: VariablePickerPopoverProps): JSX.Element {
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState('');

  const variables = useMemo(() => {
    const all = getRegisteredVariables();
    const filtered = filterIds ? all.filter((v) => filterIds.has(v.id)) : all;
    if (!query.trim()) return filtered;
    const q = query.trim().toLowerCase();
    return filtered.filter(
      (v) =>
        v.id.toLowerCase().includes(q) ||
        v.label.toLowerCase().includes(q) ||
        v.description.toLowerCase().includes(q),
    );
  }, [filterIds, query]);

  return (
    <Popover
      opened={opened}
      onChange={(next) => {
        setOpened(next);
        if (!next) setQuery('');
      }}
      closeOnClickOutside
      width={width}
      position="bottom-end"
      withArrow
      shadow="md"
    >
      <Popover.Target>
        <Tooltip label={triggerLabel} withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label={triggerLabel}
            onClick={() => setOpened((v) => !v)}
          >
            <IconBraces size={14} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <Stack gap="xs">
          <TextInput
            size="xs"
            placeholder="Search variables"
            leftSection={<IconSearch size={14} />}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            autoFocus
          />
          <ScrollArea.Autosize mah={260}>
            <Stack gap={2}>
              {variables.length === 0 ? (
                <Text size="xs" c="dimmed" ta="center" py="sm">
                  No variables match.
                </Text>
              ) : (
                variables.map((v) => (
                  <UnstyledButton
                    key={v.id}
                    onClick={() => {
                      onPick(buildVariableToken(v.id), v);
                      setOpened(false);
                      setQuery('');
                    }}
                    p="xs"
                    style={{
                      borderRadius: 6,
                      transition: 'background-color 120ms',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-1)')
                    }
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <Stack gap={0}>
                      <Text size="xs" fw={600}>
                        {v.label}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {v.description}
                      </Text>
                      <Text size="xs" c="gray.6" ff="monospace">
                        {buildVariableToken(v.id)}
                      </Text>
                    </Stack>
                  </UnstyledButton>
                ))
              )}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
